import { requireCompletedProfile } from "@hisani/auth";
import { canUseStableFeature, canViewStableFeature, } from "../domain/membership/authorization-policy.js";
import { canAddSealedRecord } from "../domain/horses/horse-lifecycle.js";
import { beginSealedRecordDocumentUploadSchema, getSealedRecordDocumentSchema, stableForbidden, stableInvalidTransition, stableNotFound, stableUploadUnavailable, } from "../validation.js";
/**
 * Shared plumbing for every Sealed "unified timeline" record feature (Vet
 * Records, Farrier Records, Vaccinations — `docs/products/stable.md`:
 * Vaccinations is "built on the same unified-timeline plumbing as Vet
 * Records"). Originally lived duplicated per-file across Vet and Farrier
 * (tolerated because both shared one file); extracted here once Vaccinations
 * needed the identical pattern a third time, per the repository's own
 * consolidation rule ("consolidate semantic duplication at the layer that
 * owns the invariant" — `AGENTS.md` §11).
 */
/** Presigned PUT window a client has to finish an upload. Presigned GET
 * window a document download link stays valid for, minted fresh per
 * request (`docs/products/stable.md`: "per-request authorization checks") —
 * mirrors Marketplace's own asset redirect window
 * (`getMarketplaceAsset`: `expiresInSeconds: 60`). */
const DOCUMENT_UPLOAD_EXPIRY_SECONDS = 300;
const DOCUMENT_DOWNLOAD_EXPIRY_SECONDS = 60;
export async function requireFeatureMembership(dependencies, stableId, horsemanId, feature, level) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    const allowed = level === "USE"
        ? canUseStableFeature(membership, feature)
        : canViewStableFeature(membership, feature);
    if (!allowed)
        throw stableForbidden();
    return membership;
}
/** A Sealed timeline entry can only be created against a Horse currently
 * accepting new Sealed entries (`horse-lifecycle.ts`: `canAddSealedRecord`).
 * Not re-checked when logging an outcome on an already-existing entry,
 * mirroring `logTrainingEntryOutcome`: completing history for a
 * since-retired horse stays legal. */
export async function requireHorseAcceptingSealedRecords(dependencies, stableId, horseId) {
    const horse = await dependencies.repository.getHorse(stableId, horseId);
    if (horse === null)
        throw stableNotFound();
    if (!canAddSealedRecord(horse)) {
        throw stableInvalidTransition("This horse is not currently accepting new Sealed records.");
    }
}
function sealedRecordObjectKeyPrefix(stableId, area) {
    return `private/stable/${stableId}/${area}/`;
}
function safeFilename(filename) {
    return filename.replace(/[^A-Za-z0-9._-]/g, "-");
}
/**
 * Verifies every attached document actually landed in object storage with
 * the declared content-type/size (content-type/size validation per
 * `docs/products/stable.md`), rejects an object key that was not issued for
 * this Stable/area/document id, then tags each object claimed so cleanup
 * jobs never reap it — mirrors `assertAssetsUploaded`/`claimAssetsUploaded`
 * in `packages/marketplace/src/validation.ts`.
 */
export async function verifyAndClaimSealedRecordDocuments(stableId, area, documents, dependencies) {
    if (documents.length === 0)
        return;
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const prefix = sealedRecordObjectKeyPrefix(stableId, area);
    const claims = await Promise.all(documents.map(async (document) => {
        if (!document.objectKey.startsWith(`${prefix}${document.id}-`)) {
            throw stableForbidden();
        }
        const uploaded = await uploads.assertUploaded({
            objectKey: document.objectKey,
            contentType: document.contentType,
            contentLength: document.size,
        });
        return { objectKey: document.objectKey, versionId: uploaded.versionId };
    }));
    await Promise.all(claims.map((claim) => uploads.claimUploaded(claim)));
}
export async function beginSealedRecordDocumentUpload(principal, input, area, feature, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = beginSealedRecordDocumentUploadSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, feature, "USE");
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const objectKey = `${sealedRecordObjectKeyPrefix(parsed.stableId, area)}${parsed.id}-${safeFilename(parsed.filename)}`;
    const { uploadUrl } = await uploads.createUpload({
        objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
        expiresInSeconds: DOCUMENT_UPLOAD_EXPIRY_SECONDS,
    });
    return { id: parsed.id, objectKey, uploadUrl };
}
export async function getSealedRecordDocumentUrl(principal, input, feature, lookup, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getSealedRecordDocumentSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, feature, "VIEW");
    const uploads = dependencies.documentUploads;
    if (uploads === undefined)
        throw stableUploadUnavailable();
    const document = await lookup(parsed.stableId, parsed.documentId);
    if (document === null)
        throw stableNotFound();
    const url = await uploads.createDownloadUrl({
        objectKey: document.objectKey,
        filename: document.filename,
        contentType: document.contentType,
        expiresInSeconds: DOCUMENT_DOWNLOAD_EXPIRY_SECONDS,
    });
    return { url };
}
